import React from 'react'
import { Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const PersonalTools = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">Home and getting around</Typography>
      <Typography>
        Every logged-in user lands on the home dashboard. For the ⌘K global
        search, the sidebar&apos;s favourites, recents and badges, the Quick
        Add menu, the contextual help drawer, and setting your own default
        landing page, see{' '}
        <DocLink
          docsPage
          url="/getting-started/finding-your-way"
          text="Finding Your Way Around"
        />
        .
      </Typography>
      <Typography>
        Use <DocLink url="/" text="Home" /> and{' '}
        <DocLink url="/my-account" text="My Account" />.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">My Account</Typography>
      <Typography>
        <DocLink url="/my-account" text="My Account" /> is organised into
        tabs:
      </Typography>
      <Typography component="ul" sx={{ pl: 3, m: 0 }}>
        <li>
          <strong>Personal Details</strong> — your contact details, address,
          and profile photo.
        </li>
        <li>
          <strong>BACS</strong> — your bank details for payroll.
        </li>
        <li>
          <strong>Notifications</strong> — choose email or app delivery per
          notification category.
        </li>
        <li>
          <strong>Calendar</strong> — personal calendar feed links for your
          shifts (see below).
        </li>
        <li>
          <strong>Preferences</strong> — app preferences, including a{' '}
          <strong>default landing page</strong>: pick any page you can access
          and the first visit each browser session takes you straight there
          instead of the Home Screen.
        </li>
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">2FA and passkeys</Typography>
      <Typography>
        <DocLink url="/my-account/2fa" text="Two-factor Authentication" />{' '}
        configures your authenticator app, and the same page manages your{' '}
        <strong>passkeys</strong> — Face ID, Touch ID, Windows Hello, or a
        hardware key registered to your account. A passkey can complete the
        MFA security step-up that protects patient-identifiable areas with one
        touch instead of a code.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Shift calendar feeds</Typography>
      <Typography>
        The <strong>Calendar</strong> tab of My Account issues personal iCal
        feed links so your assigned AmbuNet shifts appear in Google Calendar,
        Outlook, or Apple Calendar and stay up to date automatically. Create a
        separate link per device or calendar app — each can be copied, reset,
        or revoked on its own, so losing a phone never means replacing every
        feed.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Tasks</Typography>
      <Typography>
        <DocLink url="/my-tasks" text="My Tasks" /> is a board of status
        columns — drag a task between columns to update its status — with a
        switchable <strong>List view</strong> for working through tasks as a
        flat list. A <strong>Today</strong> focus rail keeps what&apos;s due
        now in view whatever filter is active, and a scope filter narrows the
        board to everything, just your own tasks, or one team&apos;s. Users
        can create new tasks where they have access.
      </Typography>
      <Typography>
        Use <DocLink url="/my-tasks" text="My Tasks" /> and{' '}
        <DocLink url="/tasks/new" text="New Task" />.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Notifications and support</Typography>
      <Typography>
        The header&apos;s bell shows your latest notifications;{' '}
        <DocLink url="/notifications" text="Notifications" /> is the full
        page — your complete paginated history with unread counts and
        mark-as-read controls. Delivery preferences per category are set on
        the My Account Notifications tab. Support and product update pages
        are available as system routes.
      </Typography>
      <Typography>
        Use <DocLink url="/notifications" text="Notifications" />
        <DocLink url="/support" text="Support" />
        and <DocLink url="/updates" text="Updates" />.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Crew Vault</Typography>
      <Typography>
        Crew Vault provides a secure area for crew-facing records and
        submissions. Each entry is shared with chosen roles and teams, and
        staff only see the entries shared with them. Creating, editing, and
        archiving entries requires the Governance or Manager team, who can see
        every entry (site administrators always have access).
      </Typography>
      <Typography>
        When a new entry is shared with someone, they get a Crew Vault
        notification (email or app, per their notification preferences) and the
        entry shows as <strong>Unread</strong> until they open it. Unread
        entries are listed first and counted on the dashboard to-do list.
        Editing an entry to share it with more roles or teams notifies only the
        people who have just gained access.
      </Typography>
      <Typography>
        Use <DocLink url="/crewvault" text="Crew Vault" />
        and <DocLink url="/crewvault/add" text="New Crew Vault Entry" />.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">E-learning</Typography>
      <Typography>
        Staff take mandatory training courses from{' '}
        <DocLink url="/mandatory-training" text="My Training" /> — now found
        in the sidebar under <strong>Governance → For Everyone</strong>.
        Passing a course&apos;s assessment (80% or more) records the
        completion on their staff record straight away and issues a
        certificate, with no upload or manager approval needed.
      </Typography>
      <Typography>
        See{' '}
        <DocLink
          docsPage
          url="/employees-hr/mandatory-training-and-e-learning"
          text="Mandatory Training and E-learning"
        />{' '}
        for how courses, assessments, certificates and the training matrix
        work.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Announcements and documents</Typography>
      <Typography>
        Announcements, policy documents, SOPs, PGDs, and mandatory training
        tasks can appear on the home screen or in team actions when a user needs
        to read, sign, acknowledge, or complete something.
      </Typography>
    </Stack>
  </Stack>
)

export default PersonalTools
